import type { MetricUnit } from "src/generated/enums"

/**
 * Human-readable byte size in German locale ("512 B", "12,5 KB", "1,4 GB"). 1024-based, at most one
 * decimal place. Shared by the file browser and the deployment data-usage card so sizes read the same
 * across the app.
 *
 * **Values below 1024 are formatted too, not passed through.** A file size is integral, a throughput is
 * not: [metricValue] routes rates through here, and raw interpolation reads `983.2945982228792 B/s` —
 * with a dot, so not even de-DE.
 */
export function humanBytes(bytes: number): string {
  // Like [durationSeconds]: a broken measurement must not pass as a number with a unit.
  if (!Number.isFinite(bytes)) return "—"
  const abs = Math.abs(bytes)
  // Below 10 B the first decimal still carries something, above that it is noise. Whole numbers are
  // unaffected — `maximumFractionDigits` only truncates, it does not pad.
  if (abs < 10) return `${bytes.toLocaleString("de-DE", { maximumFractionDigits: 1 })} B`
  // Checked against the ROUNDED value, not the raw one: without a decimal, 1023,7 renders as 1.024 B —
  // a byte figure that is not supposed to exist in bytes any more. This way it falls through to KB.
  const whole = Math.round(abs)
  if (whole < 1024) return `${Math.round(bytes).toLocaleString("de-DE")} B`
  const units = ["KB", "MB", "GB", "TB", "PB"]
  let value = bytes / 1024
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i++
  }
  return `${value.toLocaleString("de-DE", { maximumFractionDigits: 1 })} ${units[i]}`
}

/**
 * Byte size for a **usage figure** — a value sitting next to a bar or in a column of other sizes.
 *
 * A second formatter next to [humanBytes], not a duplicate — two differences, both about a column:
 *
 * - **The decimal count is pinned**, so `31,0 GB / 50,0 GB` lines up with `4,4 GB / 40,0 GB` under it;
 *   [humanBytes] omits a trailing `,0`, which is right in prose and makes a column ragged.
 * - **Bogus input renders as `?`**. These values come from an agent's `statfs` and `/proc` readings, so a
 *   negative or non-finite one is a broken measurement that "0 B" would present as a real one.
 *
 * Use [humanBytes] for a one-off size in text, this one for the System card's bars.
 */
export function usageBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "?"
  const units = ["B", "KB", "MB", "GB", "TB", "PB"]
  let value = bytes
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i++
  }
  // Whole bytes have no meaningful fraction, and a value above 100 does not need one either — at that
  // magnitude the decimal is noise, and dropping it keeps the string short.
  const digits = i === 0 || value >= 100 ? 0 : 1
  return `${value.toLocaleString("de-DE", { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${units[i]}`
}

/**
 * A duration in seconds, in the unit it is read in — `84 ms`, `1,3 s`, `4,2 min`, `41 Tage`.
 *
 * The store keeps seconds as its base unit, but the series using it lie seven orders of magnitude apart:
 * a response time is a millisecond fraction of a second, a certificate's remaining lifetime is millions
 * of seconds. A fixed number of decimals labels every point of the response-time curve `0 s` — a visibly
 * varying line beside a label claiming it measures the same thing everywhere.
 *
 * The scale is picked per value, not per series, so `800 ms` and `1,2 s` can sit on one axis. That is the
 * deliberate trade: both are correct and readable, while one unit for the whole series rounds the small
 * values back to zero.
 */
export function durationSeconds(value: number): string {
  if (!Number.isFinite(value)) return "—"
  const abs = Math.abs(value)
  // Exact zero stays in seconds: 0 ms reads like a measurement below the resolution, 0 s like the zero
  // it is.
  if (abs === 0) return "0 s"

  const scaled = (v: number, digits: number, suffix: string) =>
    `${v.toLocaleString("de-DE", { maximumFractionDigits: digits })} ${suffix}`

  if (abs < 1) {
    const millis = value * 1000
    // Below 10 ms the first decimal still carries information, above that it is noise.
    return scaled(millis, Math.abs(millis) < 10 ? 1 : 0, "ms")
  }
  // 90 rather than 60 as the boundary, here and below: 75 s reads better than 1,3 min, and the jump
  // should only come once the smaller unit really gets unwieldy.
  if (abs < 90) return scaled(value, 1, "s")

  // Same rule as for the milliseconds: a decimal only while it carries something. At minutes that is 6
  // seconds of resolution — from about ten minutes on, 87 min is the figure meant, and 86,9 min claims
  // an accuracy that is not there.
  const minutes = value / 60
  if (abs < 90 * 60) return scaled(minutes, Math.abs(minutes) < 10 ? 1 : 0, "min")

  const hours = value / 3600
  if (abs < 48 * 3600) return scaled(hours, Math.abs(hours) < 10 ? 1 : 0, "h")

  // Always the plural: this branch starts at 48 hours, so the figure is never 1. A singular here reads
  // like a case that can occur and is dead code — for a duration in WORDS there is [longDuration].
  const days = value / 86_400
  return scaled(days, days >= 10 ? 0 : 1, "Tage")
}

/**
 * One metric value in its unit, German locale — for a chart axis and its tooltip.
 *
 * The store keeps base units only (bytes, seconds), so scaling happens here and nowhere else. `OnOff`
 * renders as words rather than 0/1: a gauge that is only ever two values is read as a state, and "0" on
 * an axis invites being mistaken for a measurement.
 */
export function metricValue(value: number, unit: MetricUnit): string {
  // A broken measurement is the same in every unit — not a number — so it is caught ahead of the
  // switch. Decided per unit it goes wrong quietly: `NaN >= 1` is `false`, so under `OnOff` a missing
  // value would render as the off-word and assert an outage.
  if (!Number.isFinite(value)) return "—"
  switch (unit) {
    case "Bytes":
      return humanBytes(value)
    // Byte units, per second. Deliberately not converted to bit/s, which is what a NIC's "1 Gbit"
    // rating is quoted in: the stored counter is bytes, and silently multiplying by 8 for display
    // would make the axis disagree with every other byte figure on the page.
    case "BytesPerSecond":
      return `${humanBytes(value)}/s`
    case "Seconds":
      return durationSeconds(value)
    // One decimal, the resolution hwmon reports; two would claim a precision the sensor does not have.
    // `toFixed` would be wrong here: it writes a dot in EVERY locale, so 27.8 °C even on a German
    // machine.
    case "Celsius":
      return `${value.toLocaleString("de-DE", { maximumFractionDigits: 1 })} °C`
    case "OnOff":
      return value >= 1 ? "ja" : "nein"
    case "Count":
    default:
      return value.toLocaleString("de-DE", { maximumFractionDigits: 2 })
  }
}

/**
 * A short relative time in German ("vor 2 h", "gerade eben") — for list rows, where the absolute stamp is
 * the longest text in the row and rarely the question being asked ("how old is this?" beats "on which
 * minute?"). Pair it with the absolute value in a tooltip via [absoluteDateTime], never drop that.
 *
 * Deliberately not `Intl.RelativeTimeFormat`: it would say "vor 90 Minuten" where a list wants "vor 2 h",
 * and the unit thresholds are the whole point of the abbreviation.
 */
export function relativeTime(millis: number, now: number = Date.now()): string {
  const seconds = Math.round((now - millis) / 1000)
  if (seconds >= 0 && seconds < 60) return "gerade eben"
  const age = ageOf(millis, now)
  // „vor 03.07.2024“ is not a thing: past the relative range the stamp stands on its own.
  return age.relative ? `vor ${age.text}` : age.text
}

/**
 * „in 12 min“ — the counterpart of [relativeTime] for a moment that has not come yet.
 *
 * Shares [ageOf]'s thresholds by asking for the span the other way round, so a countdown and an age can
 * never disagree about where minutes become hours.
 *
 * A moment already past is **„jetzt“**, not a negative span: for a due time that is what it means, while
 * „vor 3 s“ would read as a measurement of something that happened.
 */
export function countdownTime(millis: number, now: number = Date.now()): string {
  if (millis <= now) return "jetzt"
  return `in ${elapsedTime(now, millis)}`
}

/**
 * The **bare** age — „11 h“, „2 Tagen“, „wenigen Sekunden“ — for a caption that brings its own preposition.
 *
 * [relativeTime] carries the „vor“ because a list row has no sentence around it. Behind a „seit“ that
 * becomes „offen seit vor 11 h“, and „gerade eben“ cannot follow a preposition either, which is why the
 * sub-minute answer differs between the two.
 */
export function elapsedTime(millis: number, now: number = Date.now()): string {
  return ageOf(millis, now).text
}

/**
 * How much is left of a span — „4 h“, „2 Tage“ — for a COLUMN that carries no preposition.
 *
 * The nominative form, which is the whole reason it exists beside [countdownTime]: under a heading like
 * „Restlaufzeit“ the „in“ is already said, and dropping it from that one leaves „2 Tagen“ standing on its
 * own. Same threshold ladder, so a countdown and a column can never disagree about where hours become
 * days.
 *
 * A moment already past is `null` — the caller says what that means in its own words („abgelaufen“,
 * „jetzt“), which differs per column.
 */
export function remainingTime(millis: number, now: number = Date.now()): string | null {
  if (millis <= now) return null
  return ageOf(now, millis, { dative: false }).text
}

/**
 * The age and whether it is a relative figure at all — the ONE place the unit thresholds live, so the two
 * forms above cannot drift apart.
 *
 * `relative: false` is the absolute stamp, for a clock skew (never „in -3 h“) and for anything past a
 * month, where a relative figure says less than a date.
 */
function ageOf(
  millis: number,
  now: number,
  options: { dative?: boolean } = {},
): { text: string; relative: boolean } {
  // Dative by default, because every caller but one puts a preposition in front of it („vor 2 Tagen“,
  // „in 2 Tagen“). The exception is a bare column, and only the day form differs between the two cases.
  const dative = options.dative ?? true
  const seconds = Math.round((now - millis) / 1000)
  if (seconds < 0) return { text: absoluteDateTime(millis), relative: false }
  if (seconds < 60) return { text: dative ? "wenigen Sekunden" : "wenige Sekunden", relative: true }
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return { text: `${minutes} min`, relative: true }
  const hours = Math.round(minutes / 60)
  if (hours < 24) return { text: `${hours} h`, relative: true }
  const days = Math.round(hours / 24)
  if (days < 31) {
    const unit = days === 1 ? "Tag" : dative ? "Tagen" : "Tage"
    return { text: `${days} ${unit}`, relative: true }
  }
  return { text: absoluteDateTime(millis), relative: false }
}

/**
 * `1 Fehlschlag` / `7 Fehlschläge` — the count with the matching form, grouped German.
 *
 * German needs no plural library: two forms, with one as the special case. A slash construction saves
 * the branch and reads wrong in every single case, and the parenthesis („7 Fehlschlag(e)“) reads wrong
 * at every count too — at 1 there is a suffix too many, above it a bracket for the reader to resolve.
 *
 * The count is formatted here, so a call site needs no second `toLocaleString`: a line or finding count
 * goes past a thousand, and a bare `${count}` writes it without the dot in every locale.
 */
export function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString("de-DE")} ${count === 1 ? one : many}`
}

/**
 * A **long** duration in German words, two steps — `13 Jahre, 141 Tage`, `5 Tage, 3 Stunden`.
 *
 * The TS counterpart of `toGermanDurationString` (`shared/…/duration-format.kt`), same rules: a year is
 * 365 days, the largest step and the one below it are shown, a step of 0 drops out, and a **negative
 * duration keeps its sign** — it is a broken measurement, and rounded to 0 an arithmetic error would have
 * become a plausible figure that nobody looks at twice.
 *
 * **Not the same as [durationSeconds]**, which is why both exist: that one scales a *measured* quantity
 * for an axis and may carry a decimal, while this one answers a lifetime, where the question is how many
 * years — a disk's power-on hours as `4.888 d` is correct and nobody converts that in their head.
 */
export function longDuration(seconds: number): string {
  const total = Math.abs(Math.round(seconds))
  const steps = [
    { amount: Math.floor(total / (365 * 86_400)), one: "Jahr", many: "Jahre" },
    { amount: Math.floor(total / 86_400) % 365, one: "Tag", many: "Tage" },
    { amount: Math.floor(total / 3_600) % 24, one: "Stunde", many: "Stunden" },
    { amount: Math.floor(total / 60) % 60, one: "Minute", many: "Minuten" },
    { amount: total % 60, one: "Sekunde", many: "Sekunden" },
  ]

  const first = steps.findIndex((step) => step.amount > 0)
  // Before the sign, deliberately: a value that rounds away has no sign to carry, so „-0 Sekunden“
  // cannot arise.
  if (first < 0) return "0 Sekunden"

  const sign = seconds < 0 ? "-" : ""
  return (
    sign +
    steps
      .slice(first, first + 2)
      .filter((step) => step.amount > 0)
      .map((step) => plural(step.amount, step.one, step.many))
      .join(", ")
  )
}

/** The full local timestamp (de-DE) — the tooltip companion of [relativeTime]. */
export function absoluteDateTime(millis: number): string {
  return new Date(millis).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })
}

/**
 * The same instant WITH seconds — for values that get compared against another instant.
 *
 * A second formatter rather than seconds everywhere: „vor 3 Minuten“ with `12:40:48` behind it reads as
 * a precision the number does not have, which is why [absoluteDateTime] rounds to the minute and stays
 * the default. Reach for this one where the seconds are the answer — a unit's start time next to a log
 * line, two transitions of the same unit that differ by a fraction.
 */
export function absoluteDateTimeSeconds(millis: number): string {
  return new Date(millis).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "medium" })
}

/**
 * A docker timestamp as a de-DE date and time — the exact instant behind a rounded status phrase.
 *
 * Two shapes arrive here: `docker inspect` answers RFC3339, `docker ps` answers
 * `2024-03-14 09:12:33 +0100 CET`, whose trailing zone ABBREVIATION no engine is required to parse — it is
 * dropped, and the numeric offset in front of it carries the instant. `null` for anything that does not
 * parse and for the empty string the agent sends when it did not measure; an invented date would be worse
 * than none.
 */
export function dockerTimestamp(raw: string): string | null {
  const cleaned = raw.trim().replace(/\s+[A-Za-z]{1,5}$/, "")
  if (cleaned === "") return null
  const millis = Date.parse(cleaned)
  return Number.isNaN(millis) ? null : absoluteDateTime(millis)
}

/**
 * A **calendar day** out of an ISO timestamp — no zone, no time of day.
 *
 * A support end, an expiry, a cut-off date is a day and not an instant. They are stored as `…T23:59:59Z`
 * all the same, and that is where every conversion goes wrong: `new Date(…)` reads the value in the
 * browser's zone, so east of Greenwich 31.05.2029 becomes 01.06.2029 — a date one day off that looks
 * entirely plausible.
 *
 * Hence a pure string: the date part of the ISO value, reversed. `null` when that does not parse — a
 * guessed output would be worse than none.
 */
export function calendarDate(iso: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  return match ? `${match[3]}.${match[2]}.${match[1]}` : null
}

/**
 * Shortens a phone number to its country code and last two digits (`+49…01`).
 *
 * For views open to **several** users: a colleague's number is personal data and does not belong in a
 * list read while scrolling past. What the masking keeps is **distinguishability** — a phone escalation
 * to a group produces several delivery attempts under the same recipient name, and without a remnant of
 * the number there is no telling which attempt reached which line.
 *
 * Not access control: the full number stays on the record. This is display, not storage.
 */
export function maskedPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "")
  // Too short to shorten meaningfully — nothing beats a misleading mask.
  if (digits.length < 6) return "…"
  const country = phone.startsWith("+") ? `+${digits.slice(0, 2)}` : digits.slice(0, 2)
  return `${country}…${digits.slice(-2)}`
}

/**
 * A browser-tab title from its segments, most specific first: `pageTitle("bx1", "Integrität")`.
 *
 * Blank and absent segments drop out, so a caller can pass a selection that may not exist without
 * composing the string itself. The separator lives here alone — `App.vue`'s `titleTemplate` appends the
 * app name through the same function, so a page that composes segments and one that does not read alike.
 */
export function pageTitle(...parts: (string | null | undefined)[]): string {
  return parts
    .map((part) => part?.trim())
    .filter((part): part is string => (part ?? "") !== "")
    .join(" · ")
}
